import { ALL_PAGE_PATHS, type MyPermissionsDto, type PagePath } from '@pokerroom/types';
import type { QueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useAuthStore } from '../store/auth.store';
import { useEditionStore } from '../store/edition.store';

/** A setup destination is usable only after the server verifies the target venue. */
export async function openVenueSetup(
  targetVenueId: string,
  page: PagePath,
  client: QueryClient,
  navigate: (path: string) => void,
) {
  if (!ALL_PAGE_PATHS.includes(page)) throw new Error('Unknown setup destination');
  const session = useAuthStore.getState().sessionRevision;
  const assertSession = () => {
    if (
      useAuthStore.getState().sessionRevision !== session ||
      !useAuthStore.getState().isAuthenticated
    ) {
      throw new Error('Session changed during venue setup');
    }
  };
  let profile = await api.get<{ venueId: string; venue: { id: string } }>('/auth/profile');
  assertSession();
  if (profile.venueId !== targetVenueId || profile.venue?.id !== targetVenueId) {
    await client.cancelQueries();
    const result = await api.post<{ accessToken: string }>('/auth/switch-venue', {
      venueId: targetVenueId,
    });
    assertSession();
    useAuthStore.getState().setPermissions([], []);
    useAuthStore.getState().updateAccessToken(result.accessToken);
    client.clear();
    useEditionStore.setState({ isLoaded: false, enabledFeatures: [] });
    profile = await api.get('/auth/profile');
    assertSession();
  }
  if (profile.venueId !== targetVenueId || profile.venue?.id !== targetVenueId) {
    throw new Error('Target venue could not be verified');
  }
  const permissions = await api.get<MyPermissionsDto>('/auth/my-permissions');
  assertSession();
  useAuthStore.getState().setPermissions(permissions.visiblePages, permissions.allowedActions);
  await useEditionStore.getState().fetchEditionInfo();
  assertSession();
  if (!permissions.visiblePages.includes(page))
    throw new Error('Setup page is not permitted in this venue');
  navigate(page);
}
